import React, { useState } from 'react';
import './OrderCard.css';

function OrderCard({ data }) {
  const [activeTooltip, setActiveTooltip] = useState(null);

  const toggleTooltip = (index) => {
    setActiveTooltip(activeTooltip === index ? null : index);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getAppointmentStatus = () => {
    const hasReports = data.report_urls && data.report_urls.length > 0;
    if (hasReports) return 'Completed';
    
    const appointmentTime = new Date(data.slot_start_time);
    const currentTime = new Date();
    
    if (currentTime >= appointmentTime) return 'Processing';
    return 'Appointment Booked';
  };

  const getStatusStepIndex = () => {
    const hasReports = data.report_urls && data.report_urls.length > 0;
    if (hasReports) return 2;
    
    const appointmentTime = new Date(data.slot_start_time);
    const currentTime = new Date();
    
    if (currentTime >= appointmentTime) return 1;
    return 0;
  };

  const facility = data.facility || {};
  const profile = data.profile || {};
  const dataLake = data.data_lake_data || {};
  const slotInfo = data.slot_object?.[0] || {};

  const getDirectionsUrl = () => {
    if (facility.gmap_url) {
      return facility.gmap_url;
    }
    if (facility.lat && facility.lng) {
      return `https://www.google.com/maps/search/${facility.lat},${facility.lng}`;
    }
    return null;
  };

  return (
    <div className="card">
      <div className="header-banner">
        <p className="section-title">CENTER DETAILS</p>
        <h3>{facility.name || 'Facility Name'}</h3>
        <p>{facility.branch_name || 'Branch'}, {facility.city_name || 'City'}, {facility.state || 'State'}</p>
        {getDirectionsUrl() && (
          <a href={getDirectionsUrl()} className="directions-cta" target="_blank" rel="noopener noreferrer">
            <svg className="directions-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
            </svg>
            Get Directions
          </a>
        )}
      </div>

      <div className="card-body">
        <div className="order-id-row">
          <span>ORDER ID {data.sukra_id || 'N/A'}</span>
          <span className="status-badge">{getAppointmentStatus()}</span>
        </div>

        <div className="stepper">
          {['Appointment Booked', 'Processing', 'Completed'].map((step, index) => (
            <React.Fragment key={index}>
              <div className={`step ${index <= getStatusStepIndex() ? 'active' : ''}`}>
                <div className="icon">
                  {index === 0 && <i className={`fas fa-calendar-check step-icon ${index <= getStatusStepIndex() ? 'active' : ''}`}></i>}
                  {index === 1 && <i className={`fas fa-flask step-icon ${index <= getStatusStepIndex() ? 'active' : ''}`}></i>}
                  {index === 2 && <i className={`fas fa-check-circle step-icon ${index <= getStatusStepIndex() ? 'active' : ''}`}></i>}
                </div>
                <p>{step}</p>
              </div>
              {index < 2 && <div className="step-line"></div>}
            </React.Fragment>
          ))}
        </div>

        <div className="appointment-time">
          <p>
            <strong>Appointment Date & Time:</strong> {formatDate(data.slot_start_time)} at {formatTime(data.slot_start_time)}
          </p>
        </div>

        <div className="patient-info">
          <div className="patient-name-section">
            <p className="label">PATIENT</p>
            <p className="patient-name">{profile.name || dataLake.patient_name || 'N/A'}</p>
          </div>
          <div className="patient-details-section">
            <div className="detail-item">
              <span className="detail-label">AGE:</span>
              <span className="detail-value">{profile.age || dataLake.age || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">GENDER:</span>
              <span className="detail-value">{profile.gender ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) : dataLake.gender || 'N/A'}</span>
            </div>
          </div>
        </div>

        {data.availableTests && data.availableTests.length > 0 && (
          <>
            <div className="test-header">
              <p>TESTS (Click <svg viewBox="0 0 24 24" fill="currentColor" style={{width: '14px', height: '14px', display: 'inline-block', verticalAlign: 'middle', margin: '0 2px'}}><circle cx="12" cy="12" r="10"/><text x="12" y="16" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">i</text></svg> icon to view Test Preparations)</p>
            </div>

            <div className="tests-list">
              {data.availableTests.map((test, index) => (
                <div key={index} className="billing-row billing-row-test">
                  <div className="test-name-container">
                    <span className="test-name">
                      {test.name}
                      <span
                        className="btn-info-svg"
                        onClick={() => toggleTooltip(index)}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="12" cy="12" r="10"/>
                          <text x="12" y="16" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">i</text>
                        </svg>
                      </span>
                    </span>
                    {activeTooltip === index && (
                      <div className="test-info-tooltip">
                        <strong>Preparation:</strong> Please follow standard fasting guidelines. Consult with the center for specific preparation instructions.
                      </div>
                    )}
                  </div>
                  <span className="test-price">₹{parseFloat(test.sale_price).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="billing-row subtotal">
          <span>SUBTOTAL</span>
          <span>₹{data.amount ? parseFloat(data.amount).toFixed(2) : '0.00'}</span>
        </div>

        {data.discount_amount > 0 && (
          <div className="billing-row discount">
            <span>DISCOUNT</span>
            <span>-₹{parseFloat(data.discount_amount).toFixed(2)}</span>
          </div>
        )}

        <div className="billing-row total">
          <strong>TOTAL AMOUNT</strong>
          <strong>₹{data.amount ? parseFloat(data.amount).toFixed(2) : '0.00'}</strong>
        </div>

        <div className="payment-info">
          <div className="info-box">
            <p className="label">PAYMENT STATUS</p>
            <p className={`payment-status ${data.appointment_status}`}>
              {data.appointment_status === 'completed' ? 'Paid' : 'Pending'}
            </p>
          </div>
          <div className="info-box">
            <p className="label">COLLECTION TYPE</p>
            <p>{data.collection_type === 1 ? 'Center Visit' : 'Home Collection'}</p>
          </div>
        </div>

        {data.report_urls && data.report_urls.length > 0 && (
          <div className="reports-section">
            <p className="label">REPORTS</p>
            {data.report_urls.map((report, index) => (
              <div key={index} className="report-item">
                <a 
                  href={`${report.report_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="report-link"
                >
                  📄 {
                    report.report_type === 'bloodReport' ? 'Blood Report' :
                    report.report_type === 'dexaReport' ? 'DEXA Report' :
                    report.report_type === 'vo2maxReport' ? 'VO2 Max Report' :
                    report.report_type === 'summaryReport' ? 'Summary Report' :
                    report.report_type === 'strengthReport' ? 'Strength Report' :
                    report.report_type === 'alzheimerReport' ? 'Alzheimer Report' :
                    report.report_type === 'fatLossReport' ? 'Fat Loss Report' :
                    'Report'
                  } - {formatDate(report.created_at)}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderCard;
